import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  Monitor, 
  DollarSign, 
  GraduationCap, 
  BarChart3, 
  Brain, 
  Calendar, 
  Clock, 
  Shield, 
  Settings, 
  LogOut,
  MapPin,
  FileText,
  Sparkles,
  Building2,
  Globe,
  Briefcase,
  FileCheck,
  Star,
  AlertTriangle,
  Newspaper,
  Upload,
  BookOpen,
  Wallet,
  Video
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const employeeLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/payslips', icon: DollarSign, label: 'Payslips' },
    { to: '/training', icon: GraduationCap, label: 'Training' },
    { to: '/kpi', icon: BarChart3, label: 'My KPI' },
    { to: '/assessment', icon: Brain, label: 'Assessment' },
    { to: '/leave', icon: Calendar, label: 'Leave Requests' },
    { to: '/overtime', icon: Clock, label: 'Overtime' },
    { to: '/timesheets', icon: FileCheck, label: 'Timesheets' },
    { to: '/governance', icon: BookOpen, label: 'Governance' },
    { to: '/company-news', icon: Newspaper, label: 'Company News' },
    { to: '/recruitment', icon: Briefcase, label: 'Internal Jobs' },
  ];

  const supervisorLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/employees', icon: Users, label: 'Employees' },
    { to: '/monitoring', icon: Monitor, label: 'Monitoring' },
    { to: '/login-recordings', icon: Video, label: 'Login Recordings' },
    { to: '/world-map', icon: Globe, label: 'World Map' },
    { to: '/payroll', icon: DollarSign, label: 'Payroll' },
    { to: '/hr-finance', icon: Wallet, label: 'HR Finance' },
    { to: '/training', icon: GraduationCap, label: 'Training' },
    { to: '/kpi', icon: BarChart3, label: 'KPI Analytics' },
    { to: '/leave', icon: Calendar, label: 'Leave Management' },
    { to: '/overtime', icon: Clock, label: 'Overtime' },
    { to: '/timesheets', icon: FileCheck, label: 'Timesheets' },
    { to: '/recruitment', icon: Briefcase, label: 'Recruitment' },
    { to: '/contracts', icon: FileText, label: 'Contracts' },
    { to: '/performance-reviews', icon: Star, label: 'Performance' },
    { to: '/disciplinary', icon: AlertTriangle, label: 'Disciplinary' },
    { to: '/governance', icon: BookOpen, label: 'Governance' },
    { to: '/company-news', icon: Newspaper, label: 'Company News' },
    { to: '/upload-employees', icon: Upload, label: 'Upload Employees' },
    { to: '/audit', icon: Shield, label: 'Audit Logs' },
  ];

  const executiveLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Command Center' },
    { to: '/company', icon: Building2, label: 'Company Overview' },
    { to: '/employees', icon: Users, label: 'Workforce' },
    { to: '/monitoring', icon: Monitor, label: 'Monitoring' },
    { to: '/login-recordings', icon: Video, label: 'Login Recordings' },
    { to: '/world-map', icon: Globe, label: 'World Map' },
    { to: '/hr-finance', icon: Wallet, label: 'HR Finance' },
    { to: '/payroll', icon: DollarSign, label: 'Payroll' },
    { to: '/training', icon: GraduationCap, label: 'Training' },
    { to: '/kpi', icon: BarChart3, label: 'Analytics' },
    { to: '/recruitment', icon: Briefcase, label: 'Recruitment' },
    { to: '/ai-insights', icon: Sparkles, label: 'AI Insights' },
    { to: '/governance', icon: BookOpen, label: 'Governance' },
    { to: '/audit', icon: Shield, label: 'Blockchain Audit' },
  ];

  const getLinks = () => {
    switch (user?.role) {
      case 'executive':
        return executiveLinks;
      case 'supervisor':
        return supervisorLinks;
      default:
        return employeeLinks;
    }
  };

  const links = getLinks();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside 
        className={`sidebar ${isOpen ? 'open' : ''}`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Qfinity
              </h1>
              <p className="text-xs text-slate-500">AI Workforce</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={({ isActive }) =>
                  `nav-item ${isActive ? 'active' : ''}`
                }
                onClick={onClose}
              >
                <link.icon className="w-5 h-5" strokeWidth={1.5} />
                <span>{link.label}</span>
              </NavLink>
            ))}
          </nav>

          <Separator className="my-4 mx-3" />

          <nav className="px-3 space-y-1">
            <NavLink
              to="/settings"
              data-testid="nav-settings"
              className={({ isActive }) =>
                `nav-item ${isActive ? 'active' : ''}`
              }
              onClick={onClose}
            >
              <Settings className="w-5 h-5" strokeWidth={1.5} />
              <span>Settings</span>
            </NavLink>
          </nav>
        </ScrollArea>

        {/* User Profile */}
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="w-10 h-10">
              <AvatarImage src={user?.avatar} alt={user?.first_name} />
              <AvatarFallback className="bg-indigo-100 text-indigo-600 font-medium">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-slate-900 truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-slate-500 truncate capitalize">
                {user?.role || 'Employee'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
